import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from './config';

/**
 * Live, because approval happens while the app is open and the معلم should
 * not have to reinstall to notice. The rules are the gate — this only decides
 * which entry point to show.
 *
 * Fails closed on purpose: `loading` starts true and `isMuqri` starts false,
 * and an error from the listener (a missing document, a permission problem)
 * lands in the same false/not-loading state rather than throwing or holding
 * the last known value. Showing "open a session" to someone the server has
 * not approved would invite a press that the rules refuse anyway; showing
 * "request the role" to someone who already holds it, even briefly, reads as
 * a revocation. Reporting not-a-معلم in both the loading and error cases is
 * the only choice that is never a false positive.
 */
export function useMuqriRole(uid: string | null): { isMuqri: boolean; loading: boolean } {
  const [state, setState] = useState<{ uid: string | null; isMuqri: boolean; loading: boolean }>(
    { uid: null, isMuqri: false, loading: true }
  );
  useEffect(() => {
    setState({ uid, isMuqri: false, loading: true });
    if (!uid) { setState({ uid, isMuqri: false, loading: false }); return; }
    return onSnapshot(
      doc(db, 'users', uid),
      (snap) => setState({ uid, isMuqri: snap.data()?.roles?.muqri === true, loading: false }),
      () => setState({ uid, isMuqri: false, loading: false })
    );
  }, [uid]);
  // See useAdminRole: the uid arrives a render before the effect does, and
  // the state left over from the previous uid is an answer about somebody
  // else. Reporting it as this reader's verdict bounces them off their own
  // page on every refresh.
  if (state.uid !== uid) return { isMuqri: false, loading: uid !== null };
  return { isMuqri: state.isMuqri, loading: state.loading };
}
